import React, { useCallback, useState } from 'react'
import { Info } from '@manychat/icons'
import { BtnV2, l, Radio, RadioGroup } from '@manychat/manyui'

import { useAppDispatch } from 'reduxTyped'
import { useBillingCurrency } from 'common/billing'
import { AddOnSlug } from 'common/billing/entities/addOns/interfaces'
import { cancelAddOnModalActions } from 'common/billing/redux/cancelAddOnModal'
import { analyticsService } from 'utils/services/analytics'

import { HelperIcon } from './components/HelperIcon'
import { InfoBanner } from './components/InfoBanner'
import { SubscriptionList } from './components/SubscriptionList'
import { SubscriptionType, TypeSelectorProps } from './types'

import cm from './TypeSelectorStep.module.css'

export const TypeSelectorStep = ({ price, onClose, onNextStep }: TypeSelectorProps) => {
  const [subscriptionType, setSubscriptionType] = useState<null | SubscriptionType>(null)
  const dispatch = useAppDispatch()
  const currency = useBillingCurrency()

  const isNextButtonDisabled = subscriptionType === null

  const handleChangeSubscriptionType = (subscriptionType: string) => {
    const eventType =
      (subscriptionType as SubscriptionType) === SubscriptionType.ALL_SUBSCRIPTIONS
        ? 'SETTINGS.BILLING.CANCELLATION_OPTIONS.ALL_SUBSCRIPTIONS.CLICK'
        : 'SETTINGS.BILLING.CANCELLATION_OPTIONS.AI_ADDON.CLICK'
    analyticsService.sendEvent(eventType)
    setSubscriptionType(subscriptionType as SubscriptionType)
  }
  const handleNextStep = useCallback(() => {
    if (subscriptionType) {
      analyticsService.sendEvent('SETTINGS.BILLING.CANCELLATION_OPTIONS.NEXT.CLICK', {
        option: subscriptionType,
      })
    }

    if (subscriptionType === SubscriptionType.AI) {
      dispatch(cancelAddOnModalActions.open(AddOnSlug.AI))
      onClose()
      return
    }

    onNextStep()
  }, [dispatch, onClose, onNextStep, subscriptionType])

  return (
    <>
      <RadioGroup
        name="cancel-subscription-type"
        value={subscriptionType}
        onChange={handleChangeSubscriptionType}
        className="p-a-sm bg-default"
      >
        <div className="m-a-xs bg-white rounded b-a">
          <div className="p-a-md b-b">
            <div className="d-flex justify-between align-center">
              <Radio
                name="all-subscription-cancel-type"
                value={SubscriptionType.ALL_SUBSCRIPTIONS}
                label={l.translate('All subscriptions')}
                labelClassName={cm.radioButtonLabel}
                className="gap"
              />
              <HelperIcon text={l.translate('You can cancel all subscriptions.')} />
            </div>
            <div className={cm.subscriptions}>
              <SubscriptionList />
            </div>
          </div>
          <div className=" p-a-md">
            <div className="d-flex justify-between align-center">
              <Radio
                name="ai-subscription-cancel-type"
                value={SubscriptionType.AI}
                label={l.translate('Manychat AI')}
                lozenge="ai"
                labelClassName={cm.radioButtonLabel}
                className="gap"
              />
              <HelperIcon
                text={l.translate(
                  'You can cancel only Manychat AI while keeping the main subscription.',
                )}
              />
            </div>
            {subscriptionType === SubscriptionType.AI && (
              <InfoBanner
                className="m-t"
                title={l.translate('Cancelling due to paying over {price}?', {
                  price: l.currency(price, currency, 0),
                })}
                icon={Info}
                description={l.translate(
                  "Since subscribing, It was added to the next month's bill. Going forward, Manychat AI will be {price} per billing cycle.",
                  { price: l.currency(price, currency, 0) },
                )}
              />
            )}
          </div>
        </div>
      </RadioGroup>
      <div className="p-y-xs p-x b-t row between">
        <BtnV2 onClick={onClose} variant="ghost-primary">
          {l.translate('I’ll keep it for now')}
        </BtnV2>
        <BtnV2 disabled={isNextButtonDisabled} onClick={handleNextStep} variant="primary">
          {l.translate('Next')}
        </BtnV2>
      </div>
    </>
  )
}
